import { Input } from "@/components/ui/input";
import {
	InputSideLabel,
	InputSideLabelPopover,
} from "@/components/ui/InputSideLabel";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@nextui-org/react";
import { cn } from "@/lib/utils";
import { t } from "i18next";
import { Baby, CircleHelp, Cross, Scale, ShieldAlert, TrendingDown, Users, X } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import type { ParameterInputProps } from "./types";
import { getInputClass } from "./utils";
import { BaseCompensationChildSupplementInput, BaseCompensationDependentsSupplementInput } from "./CompensationInputs";

/** Age → G table used for "Aldersregulert" child supplement (must match group-life.ts). */
const CHILD_SUPPLEMENT_AGE_SCALING: { age: number; g: number }[] = [
	{ age: 0, g: 6.5 },
	{ age: 1, g: 6.0 },
	{ age: 2, g: 6.0 },
	{ age: 3, g: 5.5 },
	{ age: 4, g: 5.0 },
	{ age: 5, g: 5.0 },
	{ age: 6, g: 4.5 },
	{ age: 7, g: 4.0 },
	{ age: 8, g: 4.0 },
	{ age: 9, g: 3.5 },
	{ age: 10, g: 3.5 },
	{ age: 11, g: 3.5 },
	{ age: 12, g: 2.5 },
	{ age: 13, g: 2.5 },
	{ age: 14, g: 2.0 },
	{ age: 15, g: 2.0 },
	{ age: 16, g: 1.5 },
	{ age: 17, g: 1.5 },
	{ age: 18, g: 1.0 },
	{ age: 19, g: 1.0 },
];

export const ForNumberOfYearsInput = ({
	store,
	insurance,
	parameter,
}: ParameterInputProps<"for-number-of-years">) => {
	return (
		<div className="relative flex">
			<Input
				type="number"
				onWheel={(e) => (e.target as HTMLInputElement).blur()}
				value={parameter.value}
				onChange={(e) => {
					parameter.value = e.target.value;
					store.editParameter(insurance, parameter);
				}}
				className={cn("w-28 pr-12", getInputClass(parameter))}
			/>
			<Popover>
				<PopoverTrigger asChild>
					<InputSideLabel
						className={cn(
							"cursor-pointer",
							parameter.isAltered && "border-orange-400"
						)}
					>
						{t("admin.insurances.parameters.type-to-name.x")}
					</InputSideLabel>
				</PopoverTrigger>
				<PopoverContent>
					<InputSideLabelPopover
						title={t(
							"admin.insurances.parameters.type-to-name.for-number-of-years"
						)}
						error={parameter.error}
						content={t(
							"admin.insurances.parameters.content.for-number-of-years"
						)}
						isAltered={parameter.isAltered}
						resetParam={() => store.resetParameter(insurance, parameter)}
					/>
				</PopoverContent>
			</Popover>
		</div>
	);
};

export const IfNonProviderCompensationInput = ({
	store,
	insurance,
	parameter,
}: ParameterInputProps<"if-non-provider-compensation">) => {
	return (
		<div className="relative flex">
			<Input
				type="number"
				onWheel={(e) => (e.target as HTMLInputElement).blur()}
				defaultValue={parameter.value}
				onChange={(e) => {
					parameter.value = e.target.value;
					store.editParameter(insurance, parameter);
				}}
				className={cn("w-24 pr-12", getInputClass(parameter))}
			/>
			<Popover>
				<PopoverTrigger asChild>
					<InputSideLabel
						className={cn(
							"cursor-pointer",
							parameter.isAltered && "border-orange-400"
						)}
					>
						G NP
					</InputSideLabel>
				</PopoverTrigger>
				<PopoverContent>
					<InputSideLabelPopover
						title={t(
							"admin.insurances.parameters.type-to-name.if-non-provider-compensation"
						)}
						error={parameter.error}
						content={t(
							"admin.insurances.parameters.content.if-non-provider-compensation"
						)}
						isAltered={parameter.isAltered}
						resetParam={() => store.resetParameter(insurance, parameter)}
					/>
				</PopoverContent>
			</Popover>
		</div>
	);
};

export const DependentsSupplementInput = ({
	store,
	insurance,
	parameter,
	alwaysExpanded = false,
}: ParameterInputProps<"dependents-supplement">) => {
	// Only leisure-accident-death, trygghetsforsikring and extended-wc-death have the full card
	// (Forsørgertillegg + flat/age mode + G)
	if (
		insurance.type !== "leisure-accident-death" &&
		insurance.type !== "trygghetsforsikring" &&
		insurance.type !== "extended-wc-death"
	) {
		return (
			<Button variant="outline">
				<Checkbox
					isSelected={parameter.value}
					onValueChange={(e) => {
						parameter.value = e;
						store.editParameter(insurance, parameter);
					}}
					classNames={{
						label: "text-sm text-gray-700 dark:text-gray-300",
					}}
				>
					{t("admin.insurances.parameters.type-to-name.dependents-supplement")}
				</Checkbox>
			</Button>
		);
	}

	const fixedParam = insurance.parameters?.find(
		(p) => p.type === "fixed-dependents-supplement",
	);
	const baseParam = insurance.parameters?.find(
		(p) => p.type === "base-compensation-dependents-supplement",
	);
	const ageRateParam = insurance.parameters?.find(
		(p) => p.type === "dependents-supplement-age-reduction-rate",
	);
	const minAgeDepsParam = insurance.parameters?.find(
		(p) => p.type === "min-age-dependents-supplement",
	);
	const maxAgeDepsParam = insurance.parameters?.find(
		(p) => p.type === "max-age-dependents-supplement",
	);
	const isFlat = Boolean(fixedParam?.value);
	const mode: "age" | "flat" = isFlat ? "flat" : "age";

	const handleModeChange = (value: string) => {
		if (!fixedParam) return;
		if (value === "age") {
			fixedParam.value = false;
			// If this insurance was saved before the rate param existed, inject it now
			if (!ageRateParam && insurance.parameters) {
				insurance.parameters.push({
					id: crypto.randomUUID(),
					type: "dependents-supplement-age-reduction-rate",
					value: "5",
					isAltered: false,
					error: null,
				});
			}
			store.editParameter(insurance, fixedParam);
		}
		if (value === "flat") {
			fixedParam.value = true;
			if (baseParam && (!baseParam.value || Number(baseParam.value) <= 0)) {
				baseParam.value = "15";
				baseParam.error = null;
				store.editParameter(insurance, baseParam);
			}
			store.editParameter(insurance, fixedParam);
		}
	};

	return (
		<div className="flex flex-col gap-2 rounded-md border border-input bg-background px-2 py-1.5">
			<div>
			<Checkbox
				isSelected={parameter.value}
				onValueChange={(e) => {
					parameter.value = e;
					store.editParameter(insurance, parameter);
				}}
			classNames={{
				label: "text-sm text-gray-700 dark:text-gray-300",
			}}
		>
			<span className="inline-flex items-center gap-1.5">
				<Users className="h-3.5 w-3.5 shrink-0" />
				{t("admin.insurances.parameters.type-to-name.dependents-supplement")}
			</span>
		</Checkbox>
		<p className="mt-1 pl-6 text-xs text-gray-500 dark:text-gray-400">
			{t("admin.insurances.parameters.content.dependents-supplement")}
		</p>
	</div>
	{(parameter.value || alwaysExpanded) && (
		<div className={cn("flex flex-col gap-2 pl-1", alwaysExpanded && !parameter.value && "pointer-events-none opacity-40")}>
			{/* Row 1: mode toggle */}
			<div className="flex flex-wrap items-center gap-2">
				<span className="text-xs text-gray-600 dark:text-gray-300">
					{t(
						"admin.insurances.parameters.type-to-name.dependents-supplement-mode-label",
					)}
				</span>
				<RadioGroup
					value={mode}
					onValueChange={handleModeChange}
					className="flex flex-row items-center gap-4"
				>
					<div className="flex cursor-pointer items-center gap-1 text-xs text-gray-700 dark:text-gray-300">
						<RadioGroupItem value="age" className="text-gray-900" />
						<span>
							{t(
								"admin.insurances.parameters.type-to-name.child-supplement-mode-age",
							)}
						</span>
						<Tooltip>
							<TooltipTrigger asChild>
								<button
									type="button"
									className="inline-flex rounded-full p-0.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
									aria-label={t(
										"admin.insurances.parameters.type-to-name.dependents-supplement-age-tooltip",
									)}
								>
									<CircleHelp className="h-3.5 w-3.5" />
								</button>
							</TooltipTrigger>
							<TooltipContent
								side="top"
								className="max-w-[280px] p-0"
								sideOffset={6}
							>
								<div className="p-2">
									<p className="mb-1 text-xs font-medium text-zinc-700 dark:text-zinc-300">
										{t(
											"admin.insurances.parameters.type-to-name.dependents-supplement-age-scaling-title",
										)}
									</p>
									<p className="text-xs text-zinc-600 dark:text-zinc-400">
										{t(
											"admin.insurances.parameters.type-to-name.dependents-supplement-age-scaling-content",
										)}
									</p>
								</div>
							</TooltipContent>
						</Tooltip>
					</div>
					<div className="flex cursor-pointer items-center gap-1 text-xs text-gray-700 dark:text-gray-300">
						<RadioGroupItem value="flat" className="text-gray-900" />
						<span>
							{t(
								"admin.insurances.parameters.type-to-name.child-supplement-mode-flat",
							)}
						</span>
					</div>
				</RadioGroup>
			</div>
			{/* Row 2: inputs — % A (age mode only), G FT, min/max years */}
			<div className="flex flex-wrap items-center gap-2 pl-1">
				{mode === "age" && ageRateParam && (
					<div className="relative inline-flex">
						<Input
							type="number"
							onWheel={(e) => (e.target as HTMLInputElement).blur()}
							value={ageRateParam.value as string}
							onChange={(e) => {
								ageRateParam.value = e.target.value;
								store.editParameter(insurance, ageRateParam);
							}}
							className={cn("w-24 pr-12", getInputClass(ageRateParam))}
						/>
						<Popover>
							<PopoverTrigger asChild>
								<InputSideLabel
									className={cn(
										"cursor-pointer",
										ageRateParam.isAltered && "border-orange-400"
									)}
								>
									{t("admin.insurances.parameters.type-to-name.dependents-supplement-age-reduction-rate-label")}
								</InputSideLabel>
							</PopoverTrigger>
							<PopoverContent>
								<InputSideLabelPopover
									title={t("admin.insurances.parameters.type-to-name.dependents-supplement-age-reduction-rate")}
									error={ageRateParam.error}
									content={t("admin.insurances.parameters.content.dependents-supplement-age-reduction-rate")}
									isAltered={ageRateParam.isAltered}
									resetParam={() => store.resetParameter(insurance, ageRateParam)}
								/>
							</PopoverContent>
						</Popover>
					</div>
				)}
				{baseParam && (
					<BaseCompensationDependentsSupplementInput
						store={store}
						insurance={insurance}
						alwaysExpanded={alwaysExpanded}
						parameter={
							baseParam as Parameters<
								typeof BaseCompensationDependentsSupplementInput
							>[0]["parameter"]
						}
					/>
				)}
				{minAgeDepsParam && (
					<div className="relative inline-flex">
						<Input
							type="number"
							onWheel={(e) => (e.target as HTMLInputElement).blur()}
							value={minAgeDepsParam.value as string}
							onChange={(e) => {
								minAgeDepsParam.value = e.target.value;
								store.editParameter(insurance, minAgeDepsParam);
							}}
							className={cn("w-[120px] pr-12", getInputClass(minAgeDepsParam))}
						/>
						<Popover>
							<PopoverTrigger asChild>
								<InputSideLabel
									className={cn(
										"cursor-pointer",
										minAgeDepsParam.isAltered && "border-orange-400"
									)}
								>
									{t("admin.insurances.parameters.type-to-name.min-age")}
								</InputSideLabel>
							</PopoverTrigger>
							<PopoverContent>
								<InputSideLabelPopover
									title={t("admin.insurances.parameters.type-to-name.min-age")}
									error={minAgeDepsParam.error}
									content={t("admin.insurances.parameters.content.min-age")}
									isAltered={minAgeDepsParam.isAltered}
									resetParam={() => store.resetParameter(insurance, minAgeDepsParam)}
								/>
							</PopoverContent>
						</Popover>
					</div>
				)}
				{maxAgeDepsParam && (
					<div className="relative inline-flex">
						<Input
							type="number"
							onWheel={(e) => (e.target as HTMLInputElement).blur()}
							value={maxAgeDepsParam.value as string}
							onChange={(e) => {
								maxAgeDepsParam.value = e.target.value;
								store.editParameter(insurance, maxAgeDepsParam);
							}}
							className={cn("w-[120px] pr-12", getInputClass(maxAgeDepsParam))}
						/>
						<Popover>
							<PopoverTrigger asChild>
								<InputSideLabel
									className={cn(
										"cursor-pointer",
										maxAgeDepsParam.isAltered && "border-orange-400"
									)}
								>
									{t("admin.insurances.parameters.type-to-name.max-age")}
								</InputSideLabel>
							</PopoverTrigger>
							<PopoverContent>
								<InputSideLabelPopover
									title={t("admin.insurances.parameters.type-to-name.max-age")}
									error={maxAgeDepsParam.error}
									content={t("admin.insurances.parameters.content.max-age")}
									isAltered={maxAgeDepsParam.isAltered}
									resetParam={() => store.resetParameter(insurance, maxAgeDepsParam)}
								/>
							</PopoverContent>
						</Popover>
					</div>
				)}
			</div>
		</div>
	)}
	</div>
);
};

export const FixedDependentsSupplementInput = ({
	store,
	insurance,
	parameter,
}: ParameterInputProps<"fixed-dependents-supplement">) => {
	// Rendered inside DependentsSupplementInput card for leisure-accident-death,
	// trygghetsforsikring and extended-wc-death
	if (
		insurance.type === "leisure-accident-death" ||
		insurance.type === "trygghetsforsikring" ||
		insurance.type === "extended-wc-death"
	) {
		return null;
	}
	const hasDependentsSupplement = insurance.parameters?.find(
		(p) => p.type === "dependents-supplement"
	)?.value;

	if (!hasDependentsSupplement) {
		return null;
	}
	return (
		<Button variant="outline">
			<Checkbox
				isSelected={parameter.value}
				onValueChange={(e) => {
					parameter.value = e;
					store.editParameter(insurance, parameter);
				}}
				classNames={{
					label: "text-sm text-gray-700 dark:text-gray-300",
				}}
			>
				{t("admin.insurances.parameters.type-to-name.fixed-dependents-supplement")}
			</Checkbox>
		</Button>
	);
};

export const FixedDependentsAllowanceInput = ({
	store,
	insurance,
	parameter,
}: ParameterInputProps<"fixed-dependents-allowance">) => {
	const isRelevant =
		insurance.type === "dependents-allowance-base-coverage" ||
		insurance.type === "dependents-allowance-annual-salary";
	if (!isRelevant) return null;

	const ageRateParam = insurance.parameters?.find(
		(p) => p.type === "dependents-supplement-age-reduction-rate",
	);
	const taperStartAgeParam = insurance.parameters?.find(
		(p) => p.type === "dependents-allowance-taper-start-age",
	);
	const taperUnitParam = insurance.parameters?.find(
		(p) => p.type === "dependents-allowance-taper-unit",
	);
	const taper1EndAgeParam = insurance.parameters?.find(
		(p) => p.type === "dependents-allowance-taper-1-end-age",
	);
	const taper2StartAgeParam = insurance.parameters?.find(
		(p) => p.type === "dependents-allowance-taper-2-start-age",
	);
	const taper2UnitParam = insurance.parameters?.find(
		(p) => p.type === "dependents-allowance-taper-2-unit",
	);
	const taper2RateParam = insurance.parameters?.find(
		(p) => p.type === "dependents-allowance-taper-2-rate",
	);
	const taper2EndAgeParam = insurance.parameters?.find(
		(p) => p.type === "dependents-allowance-taper-2-end-age",
	);

	const isFlat = Boolean(parameter.value);
	const taperUnit = (taperUnitParam?.value as string) === "g" ? "g" : "percent";
	const taper2Unit = (taper2UnitParam?.value as string) === "g" ? "g" : (taper2UnitParam?.value as string) === "percent" ? "percent" : taperUnit;
	const hasSegment2 = Boolean(taper2RateParam);

	const taperStartAge = parseFloat(taperStartAgeParam?.value as string);
	const taper1EndAge = parseFloat(taper1EndAgeParam?.value as string);
	const taper1EndAgeValid = !Number.isNaN(taper1EndAge) && taper1EndAge > 0;
	const canAddSegment2 =
		!hasSegment2 && taper1EndAgeValid && taper1EndAge > (Number.isNaN(taperStartAge) ? 46 : taperStartAge);

	const handleCheckboxChange = (checked: boolean) => {
		parameter.value = checked;
		if (!checked && insurance.parameters) {
			if (!ageRateParam) {
				insurance.parameters.push({
					id: crypto.randomUUID(),
					type: "dependents-supplement-age-reduction-rate",
					value: "5",
					isAltered: false,
					error: null,
				});
			}
			if (!taperStartAgeParam) {
				insurance.parameters.push({
					id: crypto.randomUUID(),
					type: "dependents-allowance-taper-start-age",
					value: "46",
					isAltered: false,
					error: null,
				});
			}
			if (!taperUnitParam) {
				insurance.parameters.push({
					id: crypto.randomUUID(),
					type: "dependents-allowance-taper-unit",
					value: "percent",
					isAltered: false,
					error: null,
				});
			}
			// Always inject taper-1-end-age so the Til spinner is present from the start
			if (!taper1EndAgeParam) {
				insurance.parameters.push({
					id: crypto.randomUUID(),
					type: "dependents-allowance-taper-1-end-age",
					value: "",
					isAltered: false,
					error: null,
				});
			}
		}
		store.editParameter(insurance, parameter);
	};

	const handleUnitChange = (value: string) => {
		if (!taperUnitParam) return;
		taperUnitParam.value = value;
		store.editParameter(insurance, taperUnitParam);
	};

	const handleTaper2UnitChange = (value: string) => {
		if (!taper2UnitParam) return;
		taper2UnitParam.value = value;
		store.editParameter(insurance, taper2UnitParam);
	};

	const handleRemoveSegment2 = () => {
		if (!insurance.parameters) return;
		const segment2Types = [
			"dependents-allowance-taper-2-start-age",
			"dependents-allowance-taper-2-unit",
			"dependents-allowance-taper-2-rate",
			"dependents-allowance-taper-2-end-age",
		] as const;
		insurance.parameters = insurance.parameters.filter(
			(p) => !segment2Types.includes(p.type as (typeof segment2Types)[number]),
		);
		store.editParameter(insurance, parameter);
	};

	const handleAddSegment2 = () => {
		if (!insurance.parameters) return;
		if (!taper2StartAgeParam) {
			insurance.parameters.push({
				id: crypto.randomUUID(),
				type: "dependents-allowance-taper-2-start-age",
				// Default Fra to Trinn 1 Til so the common case needs no editing
				value: taper1EndAgeValid ? String(taper1EndAge) : "",
				isAltered: false,
				error: null,
			});
		}
		if (!taper2UnitParam) {
			insurance.parameters.push({
				id: crypto.randomUUID(),
				type: "dependents-allowance-taper-2-unit",
				// Default to same unit as Trinn 1 so existing configs are unaffected
				value: taperUnit,
				isAltered: false,
				error: null,
			});
		}
		if (!taper2RateParam) {
			insurance.parameters.push({
				id: crypto.randomUUID(),
				type: "dependents-allowance-taper-2-rate",
				value: "",
				isAltered: false,
				error: null,
			});
		}
		if (!taper2EndAgeParam) {
			insurance.parameters.push({
				id: crypto.randomUUID(),
				type: "dependents-allowance-taper-2-end-age",
				value: "",
				isAltered: false,
				error: null,
			});
		}
		store.editParameter(insurance, parameter);
	};

	const taper1RateLabel = taperUnit === "g" ? "G/år" : "%/år";
	const taper2RateLabel = taper2Unit === "g" ? "G/år" : "%/år";

	return (
		<div className="flex flex-col gap-2 rounded-md border border-input bg-background px-2 py-1.5">
			{/*
			 * The checkbox value is fixed-dependents-allowance = true means FLAT (no tapering).
			 * We invert it visually so that "Skalering" being checked means tapering IS active,
			 * matching the same pattern as ChildSupplementInput / DependentsSupplementInput.
			 */}
			<div>
				<Checkbox
					isSelected={!parameter.value}
					onValueChange={(checked) => handleCheckboxChange(!checked)}
					classNames={{
						label: "text-sm text-gray-700 dark:text-gray-300",
					}}
				>
					<span className="inline-flex items-center gap-1.5">
						<TrendingDown className="h-3.5 w-3.5 shrink-0" />
						{t("admin.insurances.parameters.type-to-name.dependents-allowance-skalering")}
					</span>
				</Checkbox>
				<p className="mt-1 pl-6 text-xs text-gray-500 dark:text-gray-400">
					{t("admin.insurances.parameters.content.dependents-allowance-skalering")}
				</p>
			</div>

			{!isFlat && <div className="flex flex-col gap-2 pl-1">
				{/* Trinn 1 — grouped box */}
				{taperStartAgeParam && ageRateParam && (
					<div className="rounded-md border border-input bg-muted/30 px-3 py-2">
					<div className="mb-2 flex items-center gap-3">
						<p className="text-xs font-medium text-gray-500 dark:text-gray-400">Trinn 1</p>
						{taperUnitParam && (
								<RadioGroup
									value={taperUnit}
									onValueChange={handleUnitChange}
									className="flex flex-row items-center gap-3"
								>
									<div className="flex cursor-pointer items-center gap-1 text-xs text-gray-700 dark:text-gray-300">
										<RadioGroupItem value="percent" className="text-gray-900" />
										<span>%</span>
									</div>
									<div className="flex cursor-pointer items-center gap-1 text-xs text-gray-700 dark:text-gray-300">
										<RadioGroupItem value="g" className="text-gray-900" />
										<span>G</span>
									</div>
								</RadioGroup>
							)}
						</div>
						<div className="flex flex-wrap items-end gap-3">
							{/* Fra spinner */}
							<div className="flex flex-col gap-0.5">
								<span className="text-xs text-gray-500 dark:text-gray-400">Fra</span>
								<Input
									type="number"
									min={0}
									onWheel={(e) => (e.target as HTMLInputElement).blur()}
									value={taperStartAgeParam.value as string}
									onChange={(e) => {
										taperStartAgeParam.value = e.target.value;
										store.editParameter(insurance, taperStartAgeParam);
									}}
									className={cn("w-16 text-center", getInputClass(taperStartAgeParam))}
								/>
							</div>

						{/* Rate spinner */}
						<div className="flex flex-col gap-0.5">
							<span className="text-xs text-gray-500 dark:text-gray-400">{taper1RateLabel}</span>
							<Input
								type="number"
								min={0}
								step={taperUnit === "g" ? 0.25 : 1}
								onWheel={(e) => (e.target as HTMLInputElement).blur()}
								value={ageRateParam.value as string}
								onChange={(e) => {
									ageRateParam.value = e.target.value;
									store.editParameter(insurance, ageRateParam);
								}}
								className={cn("w-20 text-center", getInputClass(ageRateParam))}
							/>
							</div>

							{/* Til spinner — always shown; injected on mode switch for new insurances,
							    injected lazily via the inline button for older saved insurances */}
							{taper1EndAgeParam ? (
								<div className="flex flex-col gap-0.5">
									<span className="text-xs text-gray-500 dark:text-gray-400">Til</span>
									<Input
										type="number"
										min={!Number.isNaN(taperStartAge) ? taperStartAge + 1 : 1}
										max={(() => {
											const t2End = parseFloat(taper2EndAgeParam?.value as string);
											return hasSegment2 && !Number.isNaN(t2End) ? t2End - 1 : undefined;
										})()}
										onWheel={(e) => (e.target as HTMLInputElement).blur()}
										value={taper1EndAgeParam.value as string}
										onChange={(e) => {
											taper1EndAgeParam.value = e.target.value;
											store.editParameter(insurance, taper1EndAgeParam);
										}}
										onBlur={(e) => {
											const val = parseFloat(e.target.value);
											if (Number.isNaN(val)) return;
											const minAge = !Number.isNaN(taperStartAge) ? taperStartAge + 1 : 1;
											const t2End = parseFloat(taper2EndAgeParam?.value as string);
											const maxAge = hasSegment2 && !Number.isNaN(t2End) ? t2End - 1 : undefined;
											const clamped = maxAge !== undefined ? Math.min(Math.max(val, minAge), maxAge) : Math.max(val, minAge);
											if (clamped !== val) {
												taper1EndAgeParam.value = String(clamped);
												store.editParameter(insurance, taper1EndAgeParam);
											}
										}}
										className={cn("w-16 text-center", getInputClass(taper1EndAgeParam))}
									/>
								</div>
							) : (
								insurance.parameters && (
									<button
										type="button"
										className="self-end pb-1 text-xs text-blue-600 hover:underline dark:text-blue-400"
										onClick={() => {
											if (!insurance.parameters) return;
											insurance.parameters.push({
												id: crypto.randomUUID(),
												type: "dependents-allowance-taper-1-end-age",
												value: "",
												isAltered: false,
												error: null,
											});
											store.editParameter(insurance, parameter);
										}}
									>
										+ Til
									</button>
								)
							)}
						</div>
					</div>
				)}

				{/* Trinn 2 — grouped box, shown when segment 2 is added */}
				{hasSegment2 && taper2StartAgeParam && taper2RateParam && taper2EndAgeParam && (
					<div className="relative rounded-md border border-input bg-muted/30 px-3 py-2">
						<button
							type="button"
							onClick={handleRemoveSegment2}
							className="absolute right-2 top-2 text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400"
							aria-label="Fjern trinn 2"
						>
							<X className="h-3.5 w-3.5" />
						</button>
						<div className="mb-2 flex items-center gap-3">
							<p className="text-xs font-medium text-gray-500 dark:text-gray-400">Trinn 2</p>
							{taper2UnitParam && (
								<RadioGroup
									value={taper2Unit}
									onValueChange={handleTaper2UnitChange}
									className="flex flex-row items-center gap-3"
								>
									<div className="flex cursor-pointer items-center gap-1 text-xs text-gray-700 dark:text-gray-300">
										<RadioGroupItem value="percent" className="text-gray-900" />
										<span>%</span>
									</div>
									<div className="flex cursor-pointer items-center gap-1 text-xs text-gray-700 dark:text-gray-300">
										<RadioGroupItem value="g" className="text-gray-900" />
										<span>G</span>
									</div>
								</RadioGroup>
							)}
						</div>
						<div className="flex flex-wrap items-end gap-3">
							{/* Fra — editable, but must be >= Trinn 1 Til */}
							<div className="flex flex-col gap-0.5">
								<span className="text-xs text-gray-500 dark:text-gray-400">Fra</span>
								<Input
									type="number"
									min={taper1EndAgeValid ? taper1EndAge : 0}
									onWheel={(e) => (e.target as HTMLInputElement).blur()}
									value={taper2StartAgeParam.value as string}
									onChange={(e) => {
										taper2StartAgeParam.value = e.target.value;
										store.editParameter(insurance, taper2StartAgeParam);
									}}
									onBlur={(e) => {
										const val = parseFloat(e.target.value);
										if (Number.isNaN(val)) return;
										const minAge = taper1EndAgeValid ? taper1EndAge : 0;
										if (val < minAge) {
											taper2StartAgeParam.value = String(minAge);
											store.editParameter(insurance, taper2StartAgeParam);
										}
									}}
									className={cn("w-16 text-center", getInputClass(taper2StartAgeParam))}
								/>
							</div>

						{/* Rate spinner */}
						<div className="flex flex-col gap-0.5">
							<span className="text-xs text-gray-500 dark:text-gray-400">{taper2RateLabel}</span>
							<Input
								type="number"
								min={0}
								step={taper2Unit === "g" ? 0.25 : 1}
								onWheel={(e) => (e.target as HTMLInputElement).blur()}
								value={taper2RateParam.value as string}
									onChange={(e) => {
										taper2RateParam.value = e.target.value;
										store.editParameter(insurance, taper2RateParam);
									}}
									className={cn("w-20 text-center", getInputClass(taper2RateParam))}
								/>
							</div>

						{/* Til spinner */}
						<div className="flex flex-col gap-0.5">
							<span className="text-xs text-gray-500 dark:text-gray-400">Til</span>
							<Input
								type="number"
								min={(() => {
									const t2Start = parseFloat(taper2StartAgeParam?.value as string);
									return !Number.isNaN(t2Start) ? t2Start + 1 : 1;
								})()}
								onWheel={(e) => (e.target as HTMLInputElement).blur()}
								value={taper2EndAgeParam.value as string}
								onChange={(e) => {
									taper2EndAgeParam.value = e.target.value;
									store.editParameter(insurance, taper2EndAgeParam);
								}}
								onBlur={(e) => {
									const val = parseFloat(e.target.value);
									if (Number.isNaN(val)) return;
									const t2Start = parseFloat(taper2StartAgeParam?.value as string);
									const minAge = !Number.isNaN(t2Start) ? t2Start + 1 : 1;
									if (val < minAge) {
										taper2EndAgeParam.value = String(minAge);
										store.editParameter(insurance, taper2EndAgeParam);
									}
								}}
								className={cn("w-16 text-center", getInputClass(taper2EndAgeParam))}
							/>
						</div>
						</div>
					</div>
				)}

				{/* Add Trinn 2 — always visible, disabled until Trinn 1 Til has a valid value */}
				{!hasSegment2 && taper1EndAgeParam && (
					<button
						type="button"
						disabled={!canAddSegment2}
						onClick={canAddSegment2 ? handleAddSegment2 : undefined}
						className={cn(
							"w-fit text-xs",
							canAddSegment2
								? "cursor-pointer text-blue-600 hover:underline dark:text-blue-400"
								: "cursor-not-allowed text-gray-400 dark:text-gray-600",
						)}
					>
						{t("admin.insurances.parameters.type-to-name.dependents-allowance-add-segment-2")}
					</button>
			)}
		</div>}
	</div>
	);
};

export const ChildSupplementInput = ({
	store,
	insurance,
	parameter,
	alwaysExpanded = false,
}: ParameterInputProps<"child-supplement">) => {
	const baseChildSupplementParam = insurance.parameters?.find(
		(p) => p.type === "base-compensation-child-supplement",
	);
	const minAgeChildParam = insurance.parameters?.find(
		(p) => p.type === "min-age-child-supplement",
	);
	const maxAgeChildParam = insurance.parameters?.find(
		(p) => p.type === "max-age-child-supplement",
	);

	const isFlatByBaseCompensation =
		baseChildSupplementParam &&
		baseChildSupplementParam.value !== "" &&
		!Number.isNaN(Number(baseChildSupplementParam.value)) &&
		Number(baseChildSupplementParam.value) > 0;

	const mode: "age" | "flat" = isFlatByBaseCompensation ? "flat" : "age";

	const handleModeChange = (value: string) => {
		if (!baseChildSupplementParam) return;

		if (value === "age") {
			baseChildSupplementParam.value = "";
			baseChildSupplementParam.error = null;
			store.editParameter(insurance, baseChildSupplementParam);
		}

		if (value === "flat") {
			const numericValue = Number(baseChildSupplementParam.value);

			if (
				!baseChildSupplementParam.value ||
				Number.isNaN(numericValue) ||
				numericValue <= 0
			) {
				// Default to 6.5 G when switching to flat and no valid value is set
				baseChildSupplementParam.value = "6.5";
			}

			baseChildSupplementParam.error = null;
			store.editParameter(insurance, baseChildSupplementParam);
		}
	};

	return (
		<div className="flex flex-col gap-2 rounded-md border border-input bg-background px-2 py-1.5">
			<div>
				<Checkbox
					isSelected={parameter.value}
					onValueChange={(e) => {
						parameter.value = e;
						store.editParameter(insurance, parameter);
					}}
				classNames={{
					label: "text-sm text-gray-700 dark:text-gray-300",
				}}
			>
				<span className="inline-flex items-center gap-1.5">
					<Baby className="h-3.5 w-3.5 shrink-0" />
				{t("admin.insurances.parameters.type-to-name.child-supplement")}
			</span>
		</Checkbox>
		<p className="mt-1 pl-6 text-xs text-gray-500 dark:text-gray-400">
			{t("admin.insurances.parameters.content.child-supplement")}
		</p>
	</div>
	{(parameter.value || alwaysExpanded) && (
		<div className={cn("flex flex-col gap-2 pl-1", alwaysExpanded && !parameter.value && "pointer-events-none opacity-40")}>
			{/* Row 1: mode toggle */}
			<div className="flex flex-wrap items-center gap-2">
				<span className="text-xs text-gray-600 dark:text-gray-300">
					{t(
						"admin.insurances.parameters.type-to-name.child-supplement-mode-label",
					)}
				</span>
				<RadioGroup
					value={mode}
					onValueChange={handleModeChange}
					className="flex flex-row items-center gap-4"
				>
					<div className="flex cursor-pointer items-center gap-1 text-xs text-gray-700 dark:text-gray-300">
						<RadioGroupItem value="age" className="text-gray-900" />
						<span>
							{t(
								"admin.insurances.parameters.type-to-name.child-supplement-mode-age",
							)}
						</span>
						<Tooltip>
							<TooltipTrigger asChild>
								<button
									type="button"
									className="inline-flex rounded-full p-0.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
									aria-label={t(
										"admin.insurances.parameters.type-to-name.child-supplement-age-scaling-tooltip",
									)}
								>
									<CircleHelp className="h-3.5 w-3.5" />
								</button>
							</TooltipTrigger>
							<TooltipContent
								side="top"
								className="max-w-[280px] p-0"
								sideOffset={6}
							>
								<div className="p-2">
									<p className="mb-2 text-xs font-medium text-zinc-700 dark:text-zinc-300">
										{t(
											"admin.insurances.parameters.type-to-name.child-supplement-age-scaling-title",
										)}
									</p>
									<div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5 text-xs text-zinc-600 dark:text-zinc-400">
										{CHILD_SUPPLEMENT_AGE_SCALING.map(({ age, g }) => (
											<div key={age} className="contents">
												<span>{age} år</span>
												<span>{g} G</span>
											</div>
										))}
									</div>
									<p className="mt-1.5 text-xs text-zinc-500 dark:text-zinc-500">
										{t(
											"admin.insurances.parameters.type-to-name.child-supplement-age-scaling-20plus",
										)}
									</p>
								</div>
							</TooltipContent>
						</Tooltip>
					</div>
					<div className="flex cursor-pointer items-center gap-1 text-xs text-gray-700 dark:text-gray-300">
						<RadioGroupItem value="flat" className="text-gray-900" />
						<span>
							{t(
								"admin.insurances.parameters.type-to-name.child-supplement-mode-flat",
							)}
						</span>
					</div>
				</RadioGroup>
			</div>
			{/* Row 2: G BT (flat mode only) + min/max years */}
			<div className="flex flex-wrap items-center gap-2 pl-1">
				{mode === "flat" && baseChildSupplementParam && (
					<BaseCompensationChildSupplementInput
						store={store}
						insurance={insurance}
						alwaysExpanded={alwaysExpanded}
						parameter={
							baseChildSupplementParam as Parameters<
								typeof BaseCompensationChildSupplementInput
							>[0]["parameter"]
						}
					/>
				)}
				{minAgeChildParam && (
					<div className="relative inline-flex">
						<Input
							type="number"
							onWheel={(e) => (e.target as HTMLInputElement).blur()}
							value={minAgeChildParam.value as string}
							onChange={(e) => {
								minAgeChildParam.value = e.target.value;
								store.editParameter(insurance, minAgeChildParam);
							}}
							className={cn("w-[120px] pr-12", getInputClass(minAgeChildParam))}
						/>
						<Popover>
							<PopoverTrigger asChild>
								<InputSideLabel
									className={cn(
										"cursor-pointer",
										minAgeChildParam.isAltered && "border-orange-400"
									)}
								>
									{t("admin.insurances.parameters.type-to-name.min-age-child-supplement")}
								</InputSideLabel>
							</PopoverTrigger>
							<PopoverContent>
								<InputSideLabelPopover
									title={t("admin.insurances.parameters.type-to-name.min-age-child-supplement")}
									error={minAgeChildParam.error}
									content={t("admin.insurances.parameters.content.min-age-child-supplement")}
									isAltered={minAgeChildParam.isAltered}
									resetParam={() => store.resetParameter(insurance, minAgeChildParam)}
								/>
							</PopoverContent>
						</Popover>
					</div>
				)}
				{maxAgeChildParam && (
					<div className="relative inline-flex">
						<Input
							type="number"
							onWheel={(e) => (e.target as HTMLInputElement).blur()}
							value={maxAgeChildParam.value as string}
							onChange={(e) => {
								maxAgeChildParam.value = e.target.value;
								store.editParameter(insurance, maxAgeChildParam);
							}}
							className={cn("w-[120px] pr-12", getInputClass(maxAgeChildParam))}
						/>
						<Popover>
							<PopoverTrigger asChild>
								<InputSideLabel
									className={cn(
										"cursor-pointer",
										maxAgeChildParam.isAltered && "border-orange-400"
									)}
								>
									{t("admin.insurances.parameters.type-to-name.max-age-child-supplement")}
								</InputSideLabel>
							</PopoverTrigger>
							<PopoverContent>
								<InputSideLabelPopover
									title={t("admin.insurances.parameters.type-to-name.max-age-child-supplement")}
									error={maxAgeChildParam.error}
									content={t("admin.insurances.parameters.content.max-age-child-supplement")}
									isAltered={maxAgeChildParam.isAltered}
									resetParam={() => store.resetParameter(insurance, maxAgeChildParam)}
								/>
							</PopoverContent>
						</Popover>
					</div>
				)}
			</div>
		</div>
	)}
	</div>
);
};

export const FuneralAllowanceInput = ({
	store,
	insurance,
	parameter,
}: ParameterInputProps<"funeral-allowance">) => {
	return (
		<div className="rounded-md border border-input bg-background px-2 py-1.5">
			<Checkbox
				isSelected={parameter.value}
				onValueChange={(e) => {
					parameter.value = e;
					store.editParameter(insurance, parameter);
				}}
				classNames={{
					label: "text-sm text-gray-700 dark:text-gray-300",
				}}
			>
				<div className="flex items-center gap-2">
					<Cross className="h-4 w-4 text-gray-500" />
					<span>{t("admin.insurances.parameters.type-to-name.funeral-allowance")}</span>
				</div>
			</Checkbox>
			<p className="mt-1 pl-6 text-xs text-gray-500 dark:text-gray-400">
				{t("admin.insurances.parameters.content.funeral-allowance")}
			</p>
		</div>
	);
};

export const MinInvalidityInput = ({
	store,
	insurance,
	parameter,
}: ParameterInputProps<"min-invalidity">) => {
	return (
		<div className="rounded-md border border-input bg-background px-2 py-1.5">
			<Checkbox
				isSelected={parameter.value}
				onValueChange={(e) => {
					parameter.value = e;
					store.editParameter(insurance, parameter);
				}}
				classNames={{
					label: "text-sm text-gray-700 dark:text-gray-300",
				}}
			>
				<div className="flex items-center gap-2">
					<ShieldAlert className="h-4 w-4 text-gray-500" />
					<span>{t("admin.insurances.parameters.type-to-name.min-invalidity")}</span>
				</div>
			</Checkbox>
			<p className="mt-1 pl-6 text-xs text-gray-500 dark:text-gray-400">
				{t("admin.insurances.parameters.content.min-invalidity")}
			</p>
		</div>
	);
};

export const ChildSupplementCoordinationInput = ({
	store,
	insurance,
	parameter,
}: ParameterInputProps<"child-supplement-coordination">) => {
	// Independent coordination switch for barnetillegg (HOW-97). Only relevant
	// for the dedicated group life child supplement product.
	if (insurance.type !== "group-life-child-supplement") {
		return null;
	}

	return (
		<div className="flex w-56 flex-col self-stretch rounded-md border border-input bg-background px-2 py-1.5">
			<Checkbox
				isSelected={parameter.value}
				onValueChange={(e) => {
					parameter.value = e;
					store.editParameter(insurance, parameter);
				}}
				classNames={{
					label: "text-sm text-gray-700 dark:text-gray-300",
				}}
			>
				<div className="flex items-center gap-2">
					<Scale className="h-4 w-4 shrink-0 text-gray-500" />
					<span>
						{t(
							"admin.insurances.parameters.type-to-name.child-supplement-coordination",
						)}
					</span>
				</div>
			</Checkbox>
			<p className="mt-1 pl-6 text-xs text-gray-500 dark:text-gray-400">
				{t("admin.insurances.parameters.content.child-supplement-coordination")}
			</p>
		</div>
	);
};
